import { useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { splitCamelCase } from '@shared/utils';
import { ReportsChoose } from './reportsChoose';
import { Button, ErrorMessage, Modal } from '@shared/widgets';

type TReportColumnList = { id: number; title: string };
const reorder = (list: TReportColumnList[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

type ColumnType = Record<string, boolean>;
interface IModalColumnsProps {
  show: boolean;
  onClose: () => void;
  onSave: (activeColumns: string[]) => void;
  columns: string[];
  activeColumns: string[];
}

export const ColumnsModal = (props: IModalColumnsProps) => {
  const initialColumns: ColumnType = useMemo(
    () =>
      props.columns.reduce((acc, column) => {
        acc[column] = props.activeColumns.includes(column);
        return acc;
      }, {} as ColumnType),
    [],
  );
  const [columns, setColumns] = useState<ColumnType>(initialColumns);
  const [dragList, setDragList] = useState<TReportColumnList[]>(props.columns.map((title, idx) => ({ id: idx, title })));
  const [error, setError] = useState<string>('');

  const onChange = (name: string, value: boolean) => {
    setColumns((prev) => ({ ...prev, [name]: value }));
    setError('');
  };
  const dragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = reorder(dragList, result.source.index, result.destination.index);
    setDragList(items);
  };
  const handleSave = () => {
    const activeColumnsUpdated = dragList.filter((column) => columns[column.title]).map((column) => column.title);
    if (activeColumnsUpdated.length === 0) {
      setError('Please select at least one column');
      return;
    }
    props.onSave(activeColumnsUpdated);
    props.onClose();
  };

  return (
    <Modal isOpen={props.show} onClose={props.onClose} title='Choose columns'>
      <DragDropContext onDragEnd={dragEnd}>
        <Droppable droppableId='droppable-1' direction='vertical'>
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {dragList.map((column, index) => (
                <Draggable key={column.id} draggableId={column.id.toString()} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        ...provided.draggableProps.style,
                        position: 'relative',
                        left: snapshot.isDragging ? '0' : 'auto',
                        top: snapshot.isDragging ? '0' : 'auto',
                        userSelect: 'none',
                      }}
                    >
                      <ReportsChoose
                        text={splitCamelCase(column.title)}
                        defaultChecked={columns[column.title]}
                        onChange={() => onChange(column.title, !columns[column.title])}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <ErrorMessage text={error} />
      <Button className='mt-5' color='primary' fullWidth onClick={handleSave}>
        Save
      </Button>
    </Modal>
  );
};
